import { FieldViewModel, groupBy, ICrop } from "../../omnia"
import { SatelliteApi } from "../api"
import { Imagery, ImageryLayer, ImageryLayerStatus } from "../types"

export type State = ReturnType<typeof createState>

export interface Group {
    readonly description: string
    readonly cropId?: number
    readonly crop?: ICrop
    readonly items: Cropping[]
    readonly selected: boolean
    readonly canSelect: boolean
}

export interface Cropping {
    readonly croppingId: number
    readonly cropping: FieldViewModel
    readonly description: string
    readonly icon: string
    readonly tooltip: string
    readonly imagery: Imagery
    readonly layer: ImageryLayer
    readonly selected: boolean
    readonly canSelect: boolean
}

export function createState(api: SatelliteApi, imagery: Imagery[], date: Date) {

    const fields = new Map(imagery.map(i => [i.fieldId, i]))

    const cropping = api.getCropping().filter(c => fields.has(c.fieldId))

    const groups = groupByCrop(cropping, fields)

    let state = {
        api,
        cropping,
        groups
    }

    const selected = cropping.filter(c => isSelectedByDefault(c, date))

    state = setSelectedCropping(state, selected)

    return state

}

function isSelectedByDefault(cropping: FieldViewModel, date: Date) {

    if (!isImageryOnByDefault(cropping.crop)) {
        return false
    }

    if (date < cropping.startDate) {
        return false
    }

    if (date > cropping.endDate.addDays(1)) {
        return false
    }

    return true

}

function isImageryOnByDefault(crop: ICrop) {
    if (!crop) return false
    if (!crop.CropId) return false
    return crop.Imagery !== false
}

export function setSelectedCropping(state: State, cropping: { croppingId: number }[]) {

    const set = new Set(cropping.map(c => c.croppingId))

    const groups = state.groups.map(g => {

        const items = g.items.map(i => {

            const s = set.has(i.croppingId)

            return (s == i.selected)
                ? i
                : ({ ...i, selected: s })

        })

        return {
            ...g,
            selected: items.some(i => i.selected),
            items
        }

    })

    return {
        ...state,
        groups
    }

}

export function selectCropping(state: State, group: Group, cropping: Cropping, selected: boolean) {

    const groups = state.groups.map(g => {

        if (g !== group) {
            return g
        }

        const items = g.items.map(i => i === cropping
            ? { ...i, selected }
            : i
        )

        return {
            ...g,
            selected: items.some(i => i.selected),
            items
        }

    })

    return {
        ...state,
        groups
    }

}

export function selectGroup(state: State, group: Group, selected: boolean) {

    const groups = state.groups.map(g => {

        if (g !== group) {
            return g
        }

        const items = g.items.map(i => ({ ...i, selected: i.canSelect ? selected : false }))

        return {
            ...g,
            selected: items.some(i => i.selected),
            items
        }

    })

    return {
        ...state,
        groups
    }

}

function groupByCrop(cropping: FieldViewModel[], map: Map<number, Imagery>) {


    let groups = groupBy(cropping, c => c.crop, c => c?.CropId || 0)
        .map(g => {

            const items = g.values.map(v => ({
                cropping: v,
                croppingId: v.croppingId,
                description: v.description,
                selected: false,
                ...getCroppingInfo(v, map)
            })).sort((a, b) => (((a.canSelect ? 0 : 1) - (b.canSelect ? 0 : 1)) || a.description.localeCompare(b.description)))

            const cropId = g.key?.CropId || 0

            return {

                description: g.key?.Name || "Uncropped",
                crop: g.key,
                cropId,
                selected: false,
                canSelect: items.some(i => i.canSelect),
                expanded: Boolean(cropId),
                items
            }

        })

    groups = groups.sort((a, b) => {
        let d = (a.cropId ? 0 : 1) - (b.cropId ? 0 : 1)
        d = d || (a.cropId - b.cropId)
        return d
    })

    return groups

}

function getCroppingInfo(cropping: FieldViewModel, map: Map<number, Imagery>) {

    const imagery = map.get(cropping.fieldId)
    const layer = imagery?.layers?.find(l => l.croppingId == cropping.croppingId)

    return {
        imagery,
        layer,
        ...getLayerStatus(layer),
        canSelect: canSelectCropping(imagery, layer)
    }

}

function canSelectCropping(imagery: Imagery, layer: ImageryLayer) {

    if (layer?.layerId) {
        return false
    }

    if (!(layer?.layerStatus)) {
        return true
    }

    return false

}


function getLayerStatus(layer?: ImageryLayer) {

    if (layer) {

        if (layer.layerId) {
            return { icon: "fas fa-layer-group", tooltip: "A layer has already been created for this cropping" }
        }

        if (layer.layerStatus == ImageryLayerStatus.Running) {
            return { icon: "fas fa-cog animate-spin", tooltip: "The layer is being created" }
        }

        if (layer.layerStatus == ImageryLayerStatus.Pending) {
            return { icon: "far fa-clock", tooltip: "A layer has been requested, and will be created shortly" }
        }
    }
}
